import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { useWizardService } from '@bingads-webui-react/wizard';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { Field } from '@fluentui/react-components';
import { errorMessages } from '../../errors';
import { useRadioChildControlStyles } from '../../styles';
import { UetTagSelector } from '../uet-tag/uet-tag-selector';
import { getActiveGoalsCount } from '../uet-tag/uet-tag-selector-utils';
import { MeasurementTypeCard } from '../measurement-type-card';
import { VisitConversionInput } from './visit-conversion-input';
import { VisitConversionLiftLoading } from './visit-conversion-lift-loading';
import { VisitConversionLiftNotAllowed } from './visit-conversion-lift-not-allowed';

const VisitConversionLiftInternal = ({
  isEnabled,
  onChangeMethod,
  onSelectUetTags,
  onToggle,
  selectedUetTags,
  showError,
  updateValidation,
  useConversionLift,
  useVisitLift,
}) => {
  const { state: wizardState } = useWizardService();
  const { wizardData } = wizardState.context;
  const styles = useRadioChildControlStyles();

  const i18n = useContext(I18nContext);
  const title = i18n.getString(_TL_('Visit and conversion lift'));
  const description = i18n.getString(_TL_('Measure your website visitation rate and/or conversion rate. For both measurement types, you\'ll need to select the appropriate UET tag for your website.'));

  const isAllowed = useMemo(() => {
    const isFinishedFetching = !!(wizardData.fetchedUetTags && wizardData.orderedFetchedUetTags);
    if (isFinishedFetching) {
      return Object.keys(wizardData.fetchedUetTags).length && wizardData.orderedFetchedUetTags.length;
    }
    return isFinishedFetching;
  }, [wizardData.fetchedUetTags, wizardData.orderedFetchedUetTags]);
  const isLiftTypeDisabled = !isAllowed || !isEnabled;

  const [hasLiftTypeRequiredError, setHasLiftTypeRequiredError] = useState(false);

  const onToggleUseVisitConversionLift = useCallback(() => {
    const newValue = !isEnabled;
    if (newValue && !useConversionLift && !useVisitLift) {
      updateValidation(['VISIT_CONVERSION_LIFT_REQUIRED'], { isEnabled: newValue, useConversionLift, useVisitLift });
      setHasLiftTypeRequiredError(true);
    } else {
      setHasLiftTypeRequiredError(false);
    }
    onToggle();
  }, [isEnabled, onToggle, updateValidation, useConversionLift, useVisitLift]);

  const onChangeLiftTypeMethod = useCallback((values) => {
    updateValidation([], {
      isEnabled,
      useConversionLift: values.useConversionLift,
      useVisitLift: values.useVisitLift,
    });
    setHasLiftTypeRequiredError(false);
    onChangeMethod(values);
  }, [isEnabled, onChangeMethod, updateValidation]);

  // We need to show only those UET tags with active conversion goals for the "Measure conversion lift only" option.
  const orderedFetchedUetTagsWithActiveGoals = useMemo(() => (
    !wizardData.orderedFetchedUetTags
      ? null
      : wizardData.orderedFetchedUetTags.filter(tag => getActiveGoalsCount(tag.goals) > 0)
  ), [wizardData.orderedFetchedUetTags]);

  // We should only show the conversion warning if the user has opted for both visit and conversion lift and selected
  // UET tags without active conversion goals.
  const [bothVisitAndConversionFieldProps, setBothVisitAndConversionFieldProps] = useState(null);

  const areActiveConversionGoalsMissing = useMemo(() => (
    orderedFetchedUetTagsWithActiveGoals && orderedFetchedUetTagsWithActiveGoals.length === 0
  ), [orderedFetchedUetTagsWithActiveGoals]);

  const onSelectWithBothVisitAndConversion = useCallback((selectedTags) => {
    if (selectedTags.length > 0) {
      const withActiveGoalsCount = selectedTags.reduce((sum, tagId) => {
        const tagObject = wizardData.fetchedUetTags[tagId];
        const activeGoalsCount = getActiveGoalsCount(tagObject.goals);
        return sum + activeGoalsCount;
      }, 0);
      if (withActiveGoalsCount === 0) {
        updateValidation(['CONVERSION_GOAL_REQUIRED'], { selectedTags });
        setBothVisitAndConversionFieldProps({
          validationMessage: i18n.getString(errorMessages.CONVERSION_GOAL_REQUIRED),
          validationState: 'error',
        });
      } else {
        updateValidation([], { selectedTags });
        setBothVisitAndConversionFieldProps(null);
      }
    } else {
      updateValidation([], { selectedTags });
      setBothVisitAndConversionFieldProps(null);
    }
    onSelectUetTags(selectedTags);
  }, [i18n, onSelectUetTags, updateValidation, wizardData.fetchedUetTags]);

  if (!wizardData.isUetTagsFetched) {
    return (
      <VisitConversionLiftLoading title={title} />
    );
  }

  if (!isAllowed) {
    return (
      <VisitConversionLiftNotAllowed title={title} />
    );
  }

  return (
    <MeasurementTypeCard
      description={description}
      helpId="pop_BA_Experiments_VisitsConversions"
      isAllowed={isAllowed}
      isEnabled={isEnabled}
      onToggle={onToggleUseVisitConversionLift}
      title={title}
    >
      {showError && hasLiftTypeRequiredError && (
        <MessageBar intent="error">
          <MessageBarBody>{i18n.getString(errorMessages.VISIT_CONVERSION_LIFT_REQUIRED)}</MessageBarBody>
        </MessageBar>
      )}
      <VisitConversionInput
        combinedControl={
          <div className={styles.container}>
            <Field {...(showError ? bothVisitAndConversionFieldProps : null)}>
              <UetTagSelector
                fetchedUetTags={wizardData.fetchedUetTags || null}
                isDisabled={isLiftTypeDisabled}
                orderedFetchedUetTags={wizardData.orderedFetchedUetTags || null}
                onSelect={onSelectWithBothVisitAndConversion}
                selected={selectedUetTags.both || []}
                showError={showError}
                areActiveConversionGoalsMissing={areActiveConversionGoalsMissing}
              />
            </Field>
          </div>
        }
        conversionControl={
          <div className={styles.container}>
            <UetTagSelector
              fetchedUetTags={wizardData.fetchedUetTags || null}
              isDisabled={isLiftTypeDisabled}
              orderedFetchedUetTags={orderedFetchedUetTagsWithActiveGoals}
              onSelect={onSelectUetTags}
              selected={selectedUetTags.conversion || []}
              showError={showError}
              areActiveConversionGoalsMissing={areActiveConversionGoalsMissing}
            />
          </div>
        }
        isDisabled={isLiftTypeDisabled}
        onChangeMethod={onChangeLiftTypeMethod}
        useConversionLift={useConversionLift}
        useVisitLift={useVisitLift}
        visitControl={
          <div className={styles.container}>
            <UetTagSelector
              fetchedUetTags={wizardData.fetchedUetTags || null}
              isDisabled={isLiftTypeDisabled}
              orderedFetchedUetTags={wizardData.orderedFetchedUetTags || null}
              onSelect={onSelectUetTags}
              selected={selectedUetTags.visit || []}
              showError={showError}
            />
          </div>
        }
      />
    </MeasurementTypeCard>
  );
};

VisitConversionLiftInternal.propTypes = {
  isEnabled: PropTypes.bool,
  onChangeMethod: PropTypes.func.isRequired,
  onSelectUetTags: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  selectedUetTags: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  showError: PropTypes.bool,
  updateValidation: PropTypes.func.isRequired,
  useConversionLift: PropTypes.bool,
  useVisitLift: PropTypes.bool,
};

VisitConversionLiftInternal.defaultProps = {
  isEnabled: false,
  showError: false,
  useConversionLift: false,
  useVisitLift: false,
};

export const VisitConversionLift = withCustomValidation(VisitConversionLiftInternal);
